import React, { Fragment } from 'react'
import { Helmet } from 'react-helmet'
import { useConnect } from 'sb-redux-hook'
import { useMedia, useMemo, useCallback } from 'sb-hooks'
import { constants, mixpanel, links } from 'common/helpers'

import Text from 'components/_new/typography/Text/Text'
import { Button } from 'components/_new/controls'
import { Box, Row } from 'components/_new/layout'
import ProductsFeed from 'components/smartSearch/ProductsFeed/ProductsFeed'

import Sidebar from './Sidebar/Sidebar'
import Reviews from './Reviews/Reviews'
import FilterButtons from './FilterButtons/FilterButtons'
import FiltersOverlay from './FiltersOverlay/FiltersOverlay'

import { useCommonSeoPage, useReviewsData } from './util'

import messages from './messages'
import s from './CommonPage.scss'


const metadata = {
  placement: 'seo page',
}

const snowplowMetadata = {
  placement: 'seo page feed',
}


const CommonPage: React.FunctionComponent = () => {
  const {
    items,
    helmetTitle,
    helmetMeta,
    pageTitle,
    pageTopDescription,
    pageBottomDescription,
    reviewsTitle,
    pageIndex,
    price,
    productsAtPage,
    isBurberryColognes,
  } = useCommonSeoPage()

  const { isMobile } = useMedia()

  const { googleJsonData, ratingValue, ratingCount, reviewCount } = useReviewsData(items)

  const { productReviews, isSubscribed } = useConnect({
    productReviews: 'view.seoPage.productReviews',
    isSubscribed: 'models.subscription.isSubscribed',
  })

  const averageRatingNode = useMemo(() => (
    <Box mt={32}>
      <Text
        message={{ ...messages.rating, values: { ratingValue, ratingCount, reviewCount } }}
        size="16-24"
        color="black"
        semibold
      />
    </Box>
  ), [ ratingValue, ratingCount, reviewCount ])

  const descriptionNode = useMemo(() => Boolean(pageBottomDescription) && (
    <Box mt={24} pt={40} className={s.description}>
      <Text
        message={pageBottomDescription}
        size="22-32"
        color="black"
        semibold
        html
      />
    </Box>
  ), [ pageBottomDescription ])

  const onButtonClick = useCallback(() => {
    mixpanel.track(constants.analyticsEvents.subscribeNowLinkClick, {
      placement: 'after reviews block',
    })
  }, [])

  return (
    <div>
      <Helmet
        title={helmetTitle}
        meta={helmetMeta}
      >
        <script type="application/ld+json">
          {JSON.stringify(googleJsonData)}
        </script>
      </Helmet>
      <Row align="start">
        {
          !isMobile && (
            <Sidebar />
          )
        }
        <div className={s.content}>
          {
            isMobile && (
              <Fragment>
                <FilterButtons />
                <FiltersOverlay />
              </Fragment>
            )
          }
          <Box mb={36}>
            <Text
              message={pageTitle}
              size="28-40"
              tag="h1"
              align="center"
              semibold
            />
          </Box>
          {
            Boolean(pageTopDescription) && (
              <Box mb={24}>
                <Text
                  message={pageTopDescription}
                  size="16-24"
                  color="black"
                  html
                />
              </Box>
            )
          }
          <ProductsFeed
            items={items}
            pageIndex={pageIndex}
            limit={productsAtPage}
            metadata={metadata}
            snowplowMetadata={snowplowMetadata}
          />
          {
            Boolean(productReviews) && (
              <Box mt={40}>
                <Reviews
                  title={reviewsTitle}
                  list={productReviews}
                />
              </Box>
            )
          }
          {
            isBurberryColognes ? (
              <Fragment>
                {averageRatingNode}
                {descriptionNode}
                {
                  !isSubscribed && (
                    <Box mt={32}>
                      <Button
                        title={{ ...messages.buttonTitle, values: { price } }}
                        size="md"
                        color="brand"
                        rightIcon="rightArrowSmall"
                        to={links.subscribe}
                        onClick={onButtonClick}
                      />
                    </Box>
                  )
                }
              </Fragment>
            ) : (
              <Fragment>
                {descriptionNode}
                {averageRatingNode}
              </Fragment>
            )
          }
        </div>
      </Row>
    </div>
  )
}


export default CommonPage
